import React, { useState } from 'react';
import type { AuthorIntakeItem } from '../types';

interface Props {
  intake: AuthorIntakeItem;
  onClose: () => void;
  onUpdateStatus: (id: string, status: string, comment?: string) => Promise<void>;
  onModerate: (id: string, decision: string, comment?: string) => Promise<void>;
  onLinkOrder: (id: string, orderId?: string, articleId?: string) => Promise<void>;
  onOpenDiscussion: (item: AuthorIntakeItem) => void;
}

export const AuthorIntakeModal: React.FC<Props> = ({
  intake,
  onClose,
  onUpdateStatus,
  onModerate,
  onLinkOrder,
  onOpenDiscussion,
}) => {
  const [currentStatus, setCurrentStatus] = useState(intake.status);
  const [statusComment, setStatusComment] = useState('');
  const [moderationComment, setModerationComment] = useState('');
  const [orderLinkInput, setOrderLinkInput] = useState(intake.linked_order_id || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleStatusSave = async () => {
    setIsSaving(true);
    try {
      await onUpdateStatus(intake.id, currentStatus, statusComment);
      setStatusComment('');
    } finally {
      setIsSaving(false);
    }
  };

  const handleModerate = async (decision: 'APPROVED' | 'REJECTED') => {
    setIsSaving(true);
    try {
      await onModerate(intake.id, decision, moderationComment);
      setModerationComment('');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLinkOrderSave = async () => {
    setIsSaving(true);
    try {
      await onLinkOrder(intake.id, orderLinkInput || undefined, undefined);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="orders-pub-modal-backdrop" onClick={onClose}>
      <div
        className="orders-pub-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="orders-pub-modal-header">
          <div>
            <div className="orders-pub-eyebrow">
              {intake.intake_number} · {intake.kind}
            </div>
            <h3>{intake.subject_title}</h3>
          </div>
          <button type="button" className="orders-pub-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="orders-pub-modal-body">
          <div className="orders-pub-grid-2">
            <div className="orders-pub-field-group">
              <span className="orders-pub-field-label">Автор</span>
              <span className="orders-pub-field-value">{intake.author_name}</span>
            </div>
            <div className="orders-pub-field-group">
              <span className="orders-pub-field-label">Author ID</span>
              <span className="orders-pub-field-value">{intake.author_id_code}</span>
            </div>
          </div>

          <div className="orders-pub-grid-2">
            <div className="orders-pub-field-group">
              <span className="orders-pub-field-label">Email</span>
              <span className="orders-pub-field-value">{intake.author_email}</span>
            </div>
            <div className="orders-pub-field-group">
              <span className="orders-pub-field-label">Телефон</span>
              <span className="orders-pub-field-value">{intake.phone || 'Не указан'}</span>
            </div>
          </div>

          <div className="orders-pub-field-group">
            <span className="orders-pub-field-label">Организация / Аффилиация</span>
            <span className="orders-pub-field-value">
              {intake.organization || 'Не указана'}
            </span>
          </div>

          <div className="orders-pub-grid-2">
            <div className="orders-pub-field-group">
              <span className="orders-pub-field-label">Научное направление</span>
              <span className="orders-pub-field-value">
                {intake.scientific_area || 'Не указано'}
              </span>
            </div>
            <div className="orders-pub-field-group">
              <span className="orders-pub-field-label">Целевая база</span>
              <span className="orders-pub-field-value">
                {intake.target_database || 'Не указана'}
              </span>
            </div>
          </div>

          {intake.abstract_text && (
            <div className="orders-pub-field-group">
              <span className="orders-pub-field-label">Аннотация / Описание запроса</span>
              <div
                className="orders-pub-field-value"
                style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
              >
                {intake.abstract_text}
              </div>
            </div>
          )}

          {intake.kind === 'COAUTHOR_SEARCH' && (
            <div
              style={{
                background: '#0d1e2e',
                border: '1px solid #1a3c5d',
                borderRadius: '8px',
                padding: '1.25rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
              }}
            >
              <div className="orders-pub-eyebrow">Модерация объявления о соавторстве</div>
              <div style={{ fontSize: '0.875rem', color: '#cbd5e1' }}>
                Текущий статус модерации:{' '}
                <strong style={{ color: intake.moderation_status === 'APPROVED' ? '#34d399' : '#fbbf24' }}>
                  {intake.moderation_status}
                </strong>
              </div>
              <input
                type="text"
                className="orders-pub-input"
                placeholder="Комментарий модератора..."
                value={moderationComment}
                onChange={(e) => setModerationComment(e.target.value)}
              />
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  className="orders-pub-primary-btn"
                  style={{ background: '#059669', borderColor: '#10b981' }}
                  disabled={isSaving}
                  onClick={() => handleModerate('APPROVED')}
                >
                  Одобрить объявление
                </button>
                <button
                  type="button"
                  className="orders-pub-close-btn"
                  disabled={isSaving}
                  onClick={() => handleModerate('REJECTED')}
                >
                  Отклонить
                </button>
              </div>
            </div>
          )}

          <div
            style={{
              background: '#081624',
              border: '1px solid #142e47',
              borderRadius: '8px',
              padding: '1.25rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
            }}
          >
            <div className="orders-pub-eyebrow">Смена рабочего статуса</div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <select
                className="orders-pub-select"
                value={currentStatus}
                onChange={(e) => setCurrentStatus(e.target.value)}
              >
                <option value="NEW">NEW (Новое)</option>
                <option value="IN_PROGRESS">IN_PROGRESS (В работе)</option>
                <option value="NEED_INFO">NEED_INFO (Требуются данные)</option>
                <option value="APPROVED">APPROVED (Одобрено)</option>
                <option value="DONE">DONE (Завершено)</option>
                <option value="REJECTED">REJECTED (Отклонено)</option>
              </select>
              <input
                type="text"
                className="orders-pub-input"
                style={{ flex: 1 }}
                placeholder="Заметка к изменению статуса..."
                value={statusComment}
                onChange={(e) => setStatusComment(e.target.value)}
              />
              <button
                type="button"
                className="orders-pub-action-btn"
                disabled={isSaving}
                onClick={handleStatusSave}
              >
                Сохранить статус
              </button>
            </div>
          </div>

          <div
            style={{
              background: '#081624',
              border: '1px solid #142e47',
              borderRadius: '8px',
              padding: '1.25rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
            }}
          >
            <div className="orders-pub-eyebrow">Связывание с заказом клиента</div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <input
                type="text"
                className="orders-pub-input"
                placeholder="ID или код связанного заказа (например ORD-0042)..."
                value={orderLinkInput}
                onChange={(e) => setOrderLinkInput(e.target.value)}
              />
              <button
                type="button"
                className="orders-pub-action-btn"
                disabled={isSaving}
                onClick={handleLinkOrderSave}
              >
                Связать заказ
              </button>
            </div>
          </div>
        </div>

        <div className="orders-pub-modal-footer">
          <button
            type="button"
            className="orders-pub-primary-btn"
            onClick={() => {
              onClose();
              onOpenDiscussion(intake);
            }}
          >
            Написать автору
          </button>
          <button type="button" className="orders-pub-close-btn" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};
